import { useEffect, useRef, useState } from "react";
import { Loader2, ArrowDown } from "lucide-react";

/**
 * Lightweight pull-to-refresh wrapper for mobile.
 * Triggers `onRefresh` (returning a Promise) when the user drags down from the
 * top of the page on a touch device. Disabled on desktop / non-touch.
 *
 * Usage:
 *   <PullToRefresh onRefresh={loadData}>...page content...</PullToRefresh>
 */
const TRIGGER_DISTANCE = 70; // px past which release fires refresh
const MAX_PULL = 110;

export default function PullToRefresh({ onRefresh, children, disabled = false }) {
  const wrapperRef = useRef(null);
  const startY = useRef(null);
  const pulling = useRef(false);
  const [offset, setOffset] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (disabled) return;
    const el = wrapperRef.current;
    if (!el) return;

    const isTouch = typeof window !== "undefined" && "ontouchstart" in window;
    if (!isTouch) return;

    const handleStart = (e) => {
      // Only initiate when scrolled to top
      if (window.scrollY > 0 || document.documentElement.scrollTop > 0) return;
      if (refreshing) return;
      const t = e.touches[0];
      startY.current = t.clientY;
      pulling.current = false;
    };

    const handleMove = (e) => {
      if (startY.current == null) return;
      if (window.scrollY > 0) { startY.current = null; setOffset(0); return; }
      const dy = e.touches[0].clientY - startY.current;
      if (dy > 6) {
        pulling.current = true;
        // Resistance curve so pull feels natural
        const resisted = Math.min(MAX_PULL, dy * 0.55);
        setOffset(resisted);
        // Prevent the browser's native pull-to-refresh while we handle it
        if (e.cancelable) e.preventDefault();
      }
    };

    const handleEnd = async () => {
      if (!pulling.current) { startY.current = null; setOffset(0); return; }
      const triggered = offset >= TRIGGER_DISTANCE;
      pulling.current = false;
      startY.current = null;
      if (triggered) {
        setRefreshing(true);
        setOffset(50); // stay visible while refreshing
        try {
          await onRefresh?.();
        } catch (_) {/* ignore */}
        setRefreshing(false);
      }
      setOffset(0);
    };

    el.addEventListener("touchstart", handleStart, { passive: true });
    el.addEventListener("touchmove", handleMove, { passive: false });
    el.addEventListener("touchend", handleEnd);
    el.addEventListener("touchcancel", handleEnd);
    return () => {
      el.removeEventListener("touchstart", handleStart);
      el.removeEventListener("touchmove", handleMove);
      el.removeEventListener("touchend", handleEnd);
      el.removeEventListener("touchcancel", handleEnd);
    };
  }, [onRefresh, offset, refreshing, disabled]);

  const progress = Math.min(1, offset / TRIGGER_DISTANCE);
  const arrowRotation = progress * 180;

  return (
    <div ref={wrapperRef} className="relative">
      {/* Indicator */}
      <div
        data-testid="ptr-indicator"
        className="pointer-events-none absolute left-0 right-0 flex items-center justify-center overflow-hidden transition-[height] z-40"
        style={{
          height: `${offset}px`,
          top: 0,
          transform: "translateY(-100%)",
        }}
      >
        <div
          className="flex items-center gap-2 text-slate-500 text-xs font-medium bg-white/95 rounded-full px-3 py-1.5 shadow-sm border border-slate-200"
          style={{ transform: `translateY(${Math.min(offset, 60)}px)` }}
        >
          {refreshing ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Yenileniyor...
            </>
          ) : progress >= 1 ? (
            <>
              <ArrowDown className="h-3.5 w-3.5 rotate-180 text-emerald-600" /> Bırakın
            </>
          ) : (
            <>
              <ArrowDown className="h-3.5 w-3.5" style={{ transform: `rotate(${arrowRotation}deg)` }} /> Çekerek yenile
            </>
          )}
        </div>
      </div>

      <div
        style={{
          transform: `translateY(${offset}px)`,
          transition: pulling.current ? "none" : "transform 0.25s ease-out",
        }}
      >
        {children}
      </div>
    </div>
  );
}
